import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthWrapper';

type ModelsTableSectionProps = {
  t: (key: any) => string;
};

/** Show first N models in the table; rest behind "show more" */
const VISIBLE_COUNT = 5;

type TableRow = {
  name: string;
  kind: 'text' | 'image';
  inputPer1M: number;
  outputPer1M: number;
  pricePerImage: number;
  hidden: boolean;
  /** Real model id for navigation (API /models); display uses name */
  realModelId: string;
};

/** Popular Models: display names and prices are fixed; Chat uses real API model ids (hardcoded mapping) */
const POPULAR_MODELS: TableRow[] = [
  { kind: 'text', name: 'Big Tiger Gemma 27b', inputPer1M: 0.06, outputPer1M: 0.28, pricePerImage: 0, hidden: false, realModelId: 'big-tiger-gemma-27b-v3' },
  { kind: 'text', name: 'DeepSeek-R1', inputPer1M: 0.1, outputPer1M: 0.4, pricePerImage: 0, hidden: false, realModelId: 'deepseek-r1' },
  { kind: 'text', name: 'GPT OSS 20b', inputPer1M: 0.05, outputPer1M: 0.2, pricePerImage: 0, hidden: false, realModelId: 'gpt-oss-20b' },
  { kind: 'image', name: 'Qwen Image', inputPer1M: 0, outputPer1M: 0, pricePerImage: 0.06, hidden: true, realModelId: 'qwen-image' },
  { kind: 'image', name: 'DreamShaper', inputPer1M: 0, outputPer1M: 0, pricePerImage: 0.002, hidden: true, realModelId: 'dreamshaper' },
];

export const ModelsTableSection: React.FC<ModelsTableSectionProps> = ({ t }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showMore, setShowMore] = useState(false);

  const formatMoney = (n: number) => {
    if (n <= 0) return '—';
    const s = n >= 1 ? n.toFixed(2) : n.toFixed(4);
    return `$${s}`;
  };

  const handleChat = (row: TableRow) => {
    const destination = row.kind === 'image' ? '/image-generation' : '/text-chat';
    const chatState = { selectedModel: row.realModelId, source: 'landing' as const };
    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          from: {
            pathname: destination,
            state: chatState,
          },
        },
      });
      return;
    }
    navigate(destination, { state: chatState });
  };

  const visibleModels = useMemo(() => {
    return POPULAR_MODELS.map((model, index) => ({
      ...model,
      isHidden: model.hidden && !showMore,
      isRevealed: model.hidden && showMore,
    }));
  }, [showMore]);

  const hasMore = POPULAR_MODELS.some(m => m.hidden);

  return (
    <section className="models-table">
      <div className="container">
        <h2 className="section-title">{t('popularModels')}</h2>
        <div className="models-container">
          {visibleModels.map((model, index) => (
            <div
              key={`${model.realModelId}-${index}`}
              className={`model-row ${
                model.isHidden ? 'model-row-hidden' : ''
              } ${model.isRevealed ? 'show' : ''}`}
            >
              <div className="model-name">{model.name}</div>
              {model.kind === 'text' ? (
                <>
                  <div className="model-pricing">
                    <span className="price-label">{t('input')}</span>
                    <span className="price-value">
                      {formatMoney(model.inputPer1M)}
                    </span>
                    <span className="price-unit">{t('per1MTokens')}</span>
                  </div>
                  <div className="model-pricing">
                    <span className="price-label">{t('output')}</span>
                    <span className="price-value">
                      {formatMoney(model.outputPer1M)}
                    </span>
                    <span className="price-unit">{t('per1MTokens')}</span>
                  </div>
                </>
              ) : (
                <div className="model-pricing model-pricingSpan2">
                  <span className="price-label">{t('price')}</span>
                  <span className="price-value">
                    {formatMoney(model.pricePerImage)}
                  </span>
                  <span className="price-unit">{t('perImage')}</span>
                </div>
              )}
              <button
                className="chat-button"
                onClick={() => handleChat(model)}
                type="button"
                aria-label={`${t('chat')}: ${model.name}`}
              >
                {t('chat')}
              </button>
            </div>
          ))}

          {hasMore && (
            <div className="models-more-container">
              <button
                className={`more-button ${showMore ? 'expanded' : ''}`}
                onClick={() => setShowMore(v => !v)}
                type="button"
                aria-label="Toggle more models"
              >
                <svg
                  className="more-button-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
